/**
 * 工具函数集合
 */

/**
 * 格式化数值，保留指定小数位
 * @param {string|number} value - 要格式化的值
 * @param {number} decimals - 保留小数位数，默认 2
 * @param {string} fallback - 无效值时的显示，默认 '-'
 * @returns {string} 格式化后的字符串
 */
export function formatNumber(value, decimals = 2, fallback = '-') {
  // 处理空值
  if (value === null || value === undefined || value === '') {
    return fallback
  }

  // 转为数字
  const num = Number(value)

  // 检查是否为有效数字
  if (isNaN(num) || !isFinite(num)) {
    return fallback
  }

  // 保留指定小数位
  return num.toFixed(decimals)
}

/**
* 格式化经纬度（保留4位小数）
* @param {string|number} value - 经纬度值
* @param {string} fallback - 无效值时的显示，默认 '-'
* @returns {string} 格式化后的字符串
*/
export function formatLatLng4(value, fallback = '-') {
  return formatNumber(value, 4, fallback)
}

/**
* 格式化经纬度（保留6位小数）
* @param {string|number} value - 经纬度值
* @param {string} fallback - 无效值时的显示，默认 '-'
* @returns {string} 格式化后的字符串
*/
export function formatLatLng6(value, fallback = '-') {
  return formatNumber(value, 6, fallback)
}

/**
* 格式化距离（保留0位小数，即整数）
* @param {string|number} value - 距离值（单位：米）
* @param {string} fallback - 无效值时的显示，默认 '-'
* @returns {string} 格式化后的字符串
*/
export function formatDistance(value, fallback = '-') {
  return formatNumber(value, 0, fallback)
}

/**
* 格式化时间戳为日期时间字符串
* @param {number|string} timestamp - 时间戳（毫秒）
* @param {string} format - 格式类型，默认 'datetime'，可选 'date' | 'time'| 'M.D'
* @param {string} fallback - 无效值时的显示，默认 '-'
* @returns {string} 格式化后的日期时间字符串
*/
export function formatTime(timestamp, format = 'datetime', fallback = '-') {
  if (!timestamp) return fallback

  const date = new Date(timestamp)
  // 检查日期是否有效
  if (isNaN(date.getTime())) return fallback

  /**
   * 格式化数字为两位数的字符串表示
   * @param {number} n - 需要格式化的数字
   * @returns {string} 返回两位数的字符串表示，如果输入为个位数，则在前面补0
   */
  const pad = n => String(n).padStart(2, '0')
  const year = date.getFullYear()
  const month = pad(date.getMonth() + 1)
  const day = pad(date.getDate())
  const hours = pad(date.getHours())
  const minutes = pad(date.getMinutes())
  const seconds = pad(date.getSeconds())

  if (format === 'date') {
    return `${year}-${month}-${day}`
  }
  if (format === 'time') {
    return `${hours}:${minutes}:${seconds}`
  }
  if (format === 'M.D') {
    return `${month}.${day}`
  }
  // 默认 datetime
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
}

/**
* 格式化日期（YYYY-MM-DD）
* @param {number|string} timestamp - 时间戳
* @param {string} fallback - 无效值时的显示，默认 '-'
* @returns {string}
*/
export function formatDate(timestamp, fallback = '-') {
  return formatTime(timestamp, 'date', fallback)
}

/**
* 格式化时间（HH:mm:ss）
* @param {number|string} timestamp - 时间戳
* @param {string} fallback - 无效值时的显示，默认 '-'
* @returns {string}
*/
export function formatTimeOnly(timestamp, fallback = '-') {
  return formatTime(timestamp, 'time', fallback)
}

/**
* 判断是否为有效数字
* @param {any} value - 要检查的值
* @returns {boolean}
*/
export function isValidNumber(value) {
  if (value === null || value === undefined || value === '') return false
  const num = Number(value)
  return !isNaN(num) && isFinite(num)
}

/**
* 判断是否为有效时间戳
* @param {any} timestamp - 要检查的值
* @returns {boolean}
*/
export function isValidTimestamp(timestamp) {
  if (!timestamp) return false
  const date = new Date(timestamp)
  return !isNaN(date.getTime())
}


/**
 * 获取计算后的页面高度（简化版）
 * @param {number} headerHeight - 顶部标题栏的高度，默认80px
 * @param {number} minHeight - 最小高度限制，默认400px
 * @returns {number} 返回计算后的页面高度
 */
export function getPageHeight(headerHeight = 80, minHeight = 400) {
  // 获取视口高度
  const viewportHeight = window.innerHeight

  // 计算页面高度 = 视口高度 - 标题栏高度
  let pageHeight = viewportHeight - headerHeight

  // 确保不低于最小高度
  return Math.max(pageHeight, minHeight)
}

// 通用百分比计算，分母为 0 时返回 '0.0'
export function calcPercent(numerator, denominator) {
  if (!denominator) return '0.0'
  return (numerator / denominator * 100).toFixed(1)
}




// WEBPACK FOOTER //
// ./src/utils/util.js